import * as React from "react";
import fetch from "isomorphic-fetch";
import Client from "shopify-buy";
import { trackEvent, isKlaviyoLoaded } from "../../utils/klaviyo";
import { syncAwcToCheckout, getAwcCreateInput } from "../../utils/awin";

const client = Client.buildClient(
  {
    domain: process.env.GATSBY_SHOPIFY_STORE_URL,
    storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
  },
  fetch
);

const defaultValues = {
  cart: [],
  isOpen: false,
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  client,
  checkout: {
    lineItems: [],
  },
};

export const StoreContext = React.createContext(defaultValues);

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `sb_shopify_checkout_id`;

export const StoreProvider = ({ children }) => {
  const [checkout, setCheckout] = React.useState(defaultValues.checkout);
  const [loading, setLoading] = React.useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = React.useState(false);

  const setCheckoutItem = (checkout) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, checkout.id);
    }

    setCheckout(checkout);
  };

  const syncCheckoutAwc = React.useCallback(
    async (checkoutToSync) => {
      if (!checkoutToSync?.id) return checkoutToSync;

      const syncedCheckout = await syncAwcToCheckout(client, checkoutToSync);
      setCheckoutItem(syncedCheckout);
      return syncedCheckout;
    },
    [client]
  );

  React.useEffect(() => {
    const initializeCheckout = async () => {
      const existingCheckoutID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null;

      if (existingCheckoutID && existingCheckoutID !== `null`) {
        try {
          const existingCheckout = await client.checkout.fetch(
            existingCheckoutID
          );
          if (!existingCheckout.completedAt) {
            setCheckoutItem(await syncAwcToCheckout(client, existingCheckout));
            return;
          }
        } catch (e) {
          localStorage.setItem(localStorageKey, null);
        }
      }

      const newCheckout = await client.checkout.create(getAwcCreateInput());
      setCheckoutItem(await syncAwcToCheckout(client, newCheckout));
    };

    initializeCheckout();
  }, []);

  React.useEffect(() => {
    if (!isBrowser) return undefined;

    const handleAwcCaptured = () => {
      if (checkout?.id) {
        syncCheckoutAwc(checkout);
      }
    };

    window.addEventListener("awin:awc-captured", handleAwcCaptured);
    return () =>
      window.removeEventListener("awin:awc-captured", handleAwcCaptured);
  }, [checkout, syncCheckoutAwc]);

  const addVariantToCart = (variantId, quantity, productData = null) => {
    setLoading(true);

    const checkoutID = checkout.id;

    const lineItemsToUpdate = [
      {
        variantId,
        quantity: parseInt(quantity, 10),
      },
    ];

    return client.checkout
      .addLineItems(checkoutID, lineItemsToUpdate)
      .then((res) => syncAwcToCheckout(client, res))
      .then((res) => {
        setCheckoutItem(res);
        setLoading(false);
        setDidJustAddToCart(true);
        setTimeout(() => setDidJustAddToCart(false), 3000);

        // Track "Added to Cart" event with Klaviyo
        if (productData && isKlaviyoLoaded()) {
          // Find the line item that was just added by matching variant ID
          const addedLineItem = res.lineItems.find(
            (item) =>
              item.variant.id === variantId ||
              item.variant.storefrontId === variantId
          );

          // If we can't find it by ID, use the last added item (most recent)
          const lineItem =
            addedLineItem || res.lineItems[res.lineItems.length - 1];

          if (lineItem) {
            const klaviyoEventData = {
              ProductName: productData.productName || lineItem.title,
              ProductID: productData.productId || productData.shopifyId || "",
              SKU: lineItem.variant.sku || lineItem.variant.id || "",
              Categories: productData.categories || [],
              ImageURL:
                productData.imageUrl || lineItem.variant.image?.src || "",
              URL:
                productData.url ||
                (typeof window !== "undefined" ? window.location.href : ""),
              Brand: productData.brand || "SHANNON BOND",
              Price: parseFloat(lineItem.variant.price) || 0,
              Quantity: parseInt(quantity, 10) || 1,
              Variant: lineItem.variant.title || "",
            };

            trackEvent("Added to Cart", klaviyoEventData);
          }
        }
      });
  };

  const removeLineItem = (checkoutID, lineItemID) => {
    setLoading(true);

    return client.checkout
      .removeLineItems(checkoutID, [lineItemID])
      .then((res) => {
        setCheckout(res);
        setLoading(false);
      });
  };

  const updateLineItem = (checkoutID, lineItemID, quantity) => {
    setLoading(true);

    const lineItemsToUpdate = [
      { id: lineItemID, quantity: parseInt(quantity, 10) },
    ];

    return client.checkout
      .updateLineItems(checkoutID, lineItemsToUpdate)
      .then((res) => {
        setCheckout(res);
        setLoading(false);
      });
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        checkout,
        loading,
        didJustAddToCart,
        syncCheckoutAwc,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
